import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox, Loader, LoadingOverlay, Modal } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  IconCheck,
  IconFile,
  IconMicroscope,
  IconSearch,
  IconUser,
  IconX,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import type {
  Attachment,
  DiagnosticReport,
  Observation,
  Patient,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { formatRange, normalizeErrorString } from '@medplum/core'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { useNavigate } from 'react-router-dom'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useSessionState } from '@/hooks/useSessionState'
import { logError } from '@/utils/errorUtils'
import DOMPurify from 'dompurify'
import { PdfViewer } from '@/components/PdfViewer'
import { downloadAttachment } from '@/utils/downloadAttachment'
import { tiffToURL } from '@/utils/tiffToURL'
import { PatientSelect } from '@/pages/CallCenterHome/components/PatientSelect/PatientSelect'
import { useCanOrder } from '@/pages/Patient/components/OrderTests/shared/useCanOrder'
import {
  closeResult,
  fetchResults,
  uncloseResult,
  type ListResultsParams,
  type ResultItem,
} from '../service'
import {
  CollapsibleSectionCard,
  DATE_FORMAT,
  DateRangeRow,
  FilterPills,
  presetIdToDateFrom,
  resolvePresetRange,
} from './shared'

const PRESET_FILTERS = [
  { id: 'today', label: 'Today' },
  { id: '7days', label: '7 Days' },
  { id: '30days', label: '30 Days' },
] as const

type PresetId = (typeof PRESET_FILTERS)[number]['id']

const PAGE_SIZE = 20
const DEFAULT_PRESET_ID: PresetId = '7days'

type SeverityConfig = {
  bar: string
  label: string
  labelColor: string
}

const LAB_SEVERITY_CONFIG: Record<'abnormal' | 'available', SeverityConfig> = {
  abnormal: { bar: 'bg-red-500', label: 'ABNORMAL', labelColor: 'text-red-600' },
  available: { bar: 'bg-emerald-500', label: 'AVAILABLE', labelColor: 'text-emerald-600' },
}

const IMAGING_SEVERITY_CONFIG: Record<'abnormal' | 'available', SeverityConfig> = {
  abnormal: { bar: 'bg-red-500', label: 'ABNORMAL', labelColor: 'text-red-600' },
  available: { bar: 'bg-blue-500', label: 'AVAILABLE', labelColor: 'text-blue-600' },
}

function defaultDateFromForPreset(): string {
  return presetIdToDateFrom(DEFAULT_PRESET_ID) ?? dayjs().format(DATE_FORMAT)
}

function formatResultDate(iso: string | null): string {
  if (!iso) return ''
  const day = dayjs(iso)
  const today = dayjs().startOf('day')
  const yesterday = today.subtract(1, 'day')
  if (day.isSame(today, 'day')) return `Today, ${day.format('h:mm A')}`
  if (day.isSame(yesterday, 'day')) return `Yesterday, ${day.format('h:mm A')}`
  return day.format('MMM D, YYYY, h:mm A')
}

// ─── Expanded detail ─────────────────────────────────────────────────────────

function formatFileSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

function getAttachmentUrl(attachment: Attachment): string | undefined {
  if (attachment.url) return attachment.url
  if (!attachment.data) return undefined
  return `data:${attachment.contentType ?? 'application/octet-stream'};base64,${attachment.data}`
}

function AttachmentPill({
  attachment,
  isActive,
  onClick,
}: {
  attachment: Attachment
  isActive: boolean
  onClick: () => void
}) {
  const name = attachment.title || 'Attachment'
  const size = formatFileSize(attachment.size)
  const hasUrl = Boolean(getAttachmentUrl(attachment))

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasUrl}
      className={[
        'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors',
        isActive
          ? 'border-orange-300 bg-orange-50 text-orange-700'
          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-default',
      ].join(' ')}
    >
      <IconFile size={14} className="shrink-0 text-gray-400" />
      <span className="font-medium">{name}</span>
      {size && <span className="text-gray-400">{size}</span>}
    </button>
  )
}

function PreviewPane({ attachment }: { attachment: Attachment }) {
  const medplum = useMedplum()
  const contentType = attachment.contentType ?? ''
  const url = getAttachmentUrl(attachment)

  // Image (non-TIFF)
  const [imgSrc, setImgSrc] = useState<string>()
  const [tiffPages, setTiffPages] = useState<string[]>([])
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!url) return
    let cancelled = false
    const createdUrls: string[] = []

    if (/tiff?/i.test(contentType)) {
      tiffToURL(medplum, url)
        .then((pages) => {
          if (cancelled) {
            pages.forEach((page) => URL.revokeObjectURL(page))
            return
          }
          createdUrls.push(...pages)
          setTiffPages(pages)
        })
        .catch((err) => {
          if (!cancelled) {
            logError('home', 'Failed to load TIFF', err)
            setLoadError(true)
          }
        })
    } else if (/^image\//i.test(contentType)) {
      downloadAttachment(medplum, url)
        .then((blob) => {
          if (cancelled) return
          const objUrl = URL.createObjectURL(blob)
          createdUrls.push(objUrl)
          setImgSrc(objUrl)
        })
        .catch((err) => {
          if (!cancelled) {
            logError('home', 'Failed to load image', err)
            setLoadError(true)
          }
        })
    }

    return () => {
      cancelled = true
      createdUrls.forEach((createdUrl) => URL.revokeObjectURL(createdUrl))
    }
  }, [medplum, url, contentType])

  if (!url) return null

  if (loadError) {
    return <p className="mt-2 text-xs text-red-500">Failed to load preview.</p>
  }

  if (/pdf/i.test(contentType)) {
    return (
      <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
        <PdfViewer fileUrl={url} />
      </div>
    )
  }

  if (/tiff?/i.test(contentType)) {
    return (
      <div className="mt-3 flex flex-col gap-2">
        {tiffPages.length === 0 && <p className="text-xs text-gray-400">Loading…</p>}
        {tiffPages.map((page, i) => (
          <img
            key={page}
            src={page}
            alt={`Page ${i + 1}`}
            className="max-w-full rounded border border-gray-200"
          />
        ))}
      </div>
    )
  }

  if (/^image\//i.test(contentType)) {
    return imgSrc ? (
      <img
        src={imgSrc}
        alt={attachment.title ?? 'Attachment'}
        className="mt-3 max-w-full rounded border border-gray-200"
      />
    ) : (
      <p className="mt-2 text-xs text-gray-400">Loading…</p>
    )
  }

  // Fallback: external link for unrecognised types
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 block text-xs text-blue-600 underline"
    >
      Open attachment
    </a>
  )
}

// Mirrors LabResults.tsx TableRow — compact version for the side panel.
function ObsTableRow({ observation }: { observation: Observation }) {
  const interp = observation.interpretation?.[0]?.coding?.[0]
  const interpDisplay = interp?.display
  const isAbnormal =
    interpDisplay?.toLowerCase().includes('high') ||
    interpDisplay?.toLowerCase().includes('low') ||
    interpDisplay?.toLowerCase().includes('abnormal')
  const value = observation.valueQuantity?.value ?? observation.valueCodeableConcept?.text
  const unit =
    observation.valueQuantity?.unit ?? observation.valueCodeableConcept?.coding?.[0]?.display ?? ''
  const range = observation.referenceRange?.[0]
    ? `(${formatRange(observation.referenceRange[0])} ${unit})`
    : ''

  return (
    <tr className="border-b border-gray-100 text-xs last:border-b-0">
      <td className="py-1 pr-2 text-gray-700">
        {observation.code?.text ?? observation.code?.coding?.[0]?.display} {range}
      </td>
      <td className="py-1 pr-2 text-gray-900">{value}</td>
      <td className="py-1">
        {interpDisplay && (
          <span className={isAbnormal ? 'font-semibold text-red-500' : 'text-gray-400'}>
            {interpDisplay === 'Above high normal'
              ? '▲ '
              : interpDisplay === 'Below low normal'
                ? '▼ '
                : ''}
            {interpDisplay}
          </span>
        )}
      </td>
    </tr>
  )
}

function ExpandedResultDetail({ reportId }: { reportId: string }) {
  const medplum = useMedplum()
  const [detail, setDetail] = useState<{
    dr: DiagnosticReport | null
    panels: Observation[]
    serviceRequests: ServiceRequest[]
    attachments: Attachment[]
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [previewingAttachment, setPreviewingAttachment] = useState<Attachment | null>(null)
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setDetail(null)
    setPreviewingAttachment(null)

    medplum
      .search(
        'DiagnosticReport',
        `_id=${reportId}&_include=DiagnosticReport:based-on&_include=DiagnosticReport:result`,
      )
      .then(async (bundle) => {
        if (cancelled) return
        const entries = bundle.entry ?? []
        const dr = entries.find((e) => e.resource?.id === reportId)?.resource as
          | DiagnosticReport
          | undefined
        const observations = entries
          .map((e) => e.resource)
          .filter((r): r is Observation => r?.resourceType === 'Observation')
        const serviceRequests = entries
          .map((e) => e.resource)
          .filter((r): r is ServiceRequest => r?.resourceType === 'ServiceRequest')

        // Batch-fetch hasMember observations (same pattern as LabResults.tsx)
        const memberIds = observations
          .flatMap((obs) => (obs.hasMember ?? []).map((m) => m.reference?.split('/').pop()))
          .filter((id): id is string => Boolean(id))

        if (memberIds.length) {
          const memberBundle = await medplum.executeBatch({
            resourceType: 'Bundle',
            type: 'batch',
            entry: memberIds.map((id) => ({
              request: { method: 'GET', url: `Observation/${id}` },
            })),
          })
          const memberMap = new Map(
            (memberBundle.entry ?? []).map((e) => [e.resource?.id, e.resource]),
          )
          observations.forEach((obs) => {
            obs.hasMember?.forEach((member, i) => {
              const id = member.reference?.split('/').pop()
              if (id && obs.hasMember?.[i]) {
                obs.hasMember[i].resource = memberMap.get(id) as Observation
              }
            })
          })
        }

        const attachments: Attachment[] = dr?.presentedForm ?? []
        setDetail({ dr: dr ?? null, panels: observations, serviceRequests, attachments })
        if (attachments.length > 0) setPreviewingAttachment(attachments[0])
      })
      .catch((err) => {
        if (!cancelled) {
          logError('home', 'Failed to load result detail', err)
          setDetail({ dr: null, panels: [], serviceRequests: [], attachments: [] })
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [medplum, reportId])

  if (isLoading) {
    return (
      <div className="border-b border-gray-100 px-4 py-3">
        <div className="rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-400">Loading…</div>
      </div>
    )
  }

  const { dr, panels, serviceRequests, attachments } = detail ?? {
    dr: null,
    panels: [],
    serviceRequests: [],
    attachments: [],
  }

  const hasStructuredContent = panels.length > 0
  const hasPresentedForm = previewingAttachment !== null
  const hasConclusion = Boolean(dr?.conclusion)
  const hasAnything = hasStructuredContent || hasPresentedForm || hasConclusion

  const getServiceReqForPanel = (panel: Observation) =>
    serviceRequests.find((sr) => sr.code?.text === panel.code?.text)

  return (
    <div className="border-b border-gray-100 px-4 pb-3">
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        {!hasAnything ? (
          <p className="text-xs text-gray-400">No detailed content available.</p>
        ) : (
          <>
            {/* Table of contents — shown when multiple panels */}
            {panels.length > 1 && (
              <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 border-b border-gray-200 pb-3">
                {panels.map((panel) => (
                  <button
                    key={panel.id}
                    type="button"
                    onClick={() =>
                      panelRefs.current[panel.id ?? '']?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                      })
                    }
                    className="text-xs font-medium text-blue-600 hover:underline"
                  >
                    {panel.code?.text ?? panel.code?.coding?.[0]?.display}
                  </button>
                ))}
              </div>
            )}

            {/* Conclusion text */}
            {dr?.conclusion && (
              <p className="mb-3 text-xs leading-relaxed whitespace-pre-wrap text-gray-700">
                {dr.conclusion}
              </p>
            )}

            {/* Structured panels */}
            {panels.map((panel) => {
              const sr = getServiceReqForPanel(panel)
              const nonOrdinaryMembers = (panel.hasMember ?? [])
                .map((m) => m.resource as Observation)
                .filter((obs) => obs && !obs.valueQuantity)
              const quantMembers = (panel.hasMember ?? []).filter((m) => {
                const obs = m.resource as Observation
                return obs?.valueQuantity
              })

              return (
                <div
                  key={panel.id}
                  ref={(el) => {
                    if (panel.id) panelRefs.current[panel.id] = el
                  }}
                  className="mb-4 last:mb-0"
                >
                  <p className="mb-1 text-xs font-bold text-gray-800">
                    {panel.code?.text ?? panel.code?.coding?.[0]?.display}
                  </p>
                  {/* Dates row */}
                  <div className="mb-1 flex flex-wrap gap-3 text-[11px] text-gray-500">
                    {sr?.authoredOn && (
                      <span>Collected: {dayjs(sr.authoredOn).format('MMM D, YYYY')}</span>
                    )}
                    {panel.effectiveDateTime && (
                      <span>Received: {dayjs(panel.effectiveDateTime).format('MMM D, YYYY')}</span>
                    )}
                    {panel.issued && (
                      <span>Reported: {dayjs(panel.issued).format('MMM D, YYYY h:mm A')}</span>
                    )}
                  </div>
                  {/* HTML note (dangerouslySetInnerHTML mirrors patient chart) */}
                  {panel.note?.[0]?.text && (
                    <div
                      className="mb-2 text-xs text-gray-700"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(panel.note[0].text) }}
                    />
                  )}
                  {/* Quantitative observation table */}
                  {quantMembers.length > 0 && (
                    <table className="w-full table-auto border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-500">
                          <th className="pb-1 text-left font-medium" style={{ width: '50%' }}>
                            Test
                          </th>
                          <th className="pb-1 text-left font-medium" style={{ width: '25%' }}>
                            Value
                          </th>
                          <th className="pb-1 text-left font-medium">Interpretation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quantMembers.map((m) =>
                          m.resource ? (
                            <ObsTableRow
                              key={(m.resource as Observation).id}
                              observation={m.resource as Observation}
                            />
                          ) : null,
                        )}
                        {!panel.hasMember?.length && <ObsTableRow observation={panel} />}
                      </tbody>
                    </table>
                  )}
                  {/* Text/non-quantitative observations */}
                  {nonOrdinaryMembers.map((obs) => {
                    if (!obs) return null
                    const text = obs.note?.map((n) => n.text).join('\n') || obs.valueString
                    return text ? (
                      <p key={obs.id} className="mt-1 text-xs whitespace-pre-wrap text-gray-700">
                        {text}
                      </p>
                    ) : null
                  })}
                </div>
              )
            })}

            {/* Inline file preview — mirrors patient chart's unconditional presentedForm rendering */}
            {previewingAttachment && (
              <div
                className={
                  panels.length > 0 || dr?.conclusion ? 'mt-3 border-t border-gray-200 pt-3' : ''
                }
              >
                <PreviewPane attachment={previewingAttachment} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Attachment navigation pills */}
      {attachments.length > 0 && (
        <div className="mt-3">
          <p className="mb-2 text-[10px] font-semibold tracking-wider text-gray-400 uppercase">
            Attachments
          </p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((att, i) => (
              <AttachmentPill
                key={att.url ?? att.title ?? i}
                attachment={att}
                isActive={previewingAttachment === att}
                onClick={() => setPreviewingAttachment((prev) => (prev === att ? null : att))}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Row components ──────────────────────────────────────────────────────────

// Patient name that links to the patient chart Labs section, with the matching
// DiagnosticReport selected (and highlighted) via the existing path-based selection.
function PatientNameLink({ item }: { item: ResultItem }) {
  const navigate = useNavigate()
  const name = item.patientDisplayName || 'Unknown patient'

  if (!item.patientId) {
    return <span className="text-sm font-semibold text-gray-900">{name}</span>
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        void navigate(`/patients/${item.patientId}/labs-imaging/${item.id}`)
      }}
      className="cursor-pointer text-left text-sm font-semibold text-gray-900 hover:text-blue-600 hover:underline"
    >
      {name}
    </button>
  )
}

type RowProps = {
  item: ResultItem
  isClosed: boolean
  isSelected: boolean
  isExpanded: boolean
  onExpand: (id: string) => void
  onDismiss: (items: ResultItem[]) => void
  onRestore: (item: ResultItem) => void
  onSelectToggle: (item: ResultItem) => void
}

function LabRow({
  item,
  isClosed,
  isSelected,
  isExpanded,
  onExpand,
  onDismiss,
  onRestore,
  onSelectToggle,
}: RowProps) {
  const config = LAB_SEVERITY_CONFIG[item.severity === 'abnormal' ? 'abnormal' : 'available']
  const dateLabel = formatResultDate(item.effectiveAt)

  return (
    <div
      className={`relative flex items-center justify-between border-b border-gray-100 px-4 py-3 ${isClosed ? 'opacity-60' : ''}`}
    >
      <span className={`absolute top-0 bottom-0 left-0 w-[3px] ${config.bar}`} />
      <div className="flex flex-1 items-start gap-2 pl-2">
        {!isClosed && (
          <Checkbox
            size="xs"
            checked={isSelected}
            onChange={() => onSelectToggle(item)}
            styles={{
              input: {
                cursor: 'pointer',
                backgroundColor: isSelected ? 'rgb(229, 83, 61)' : undefined,
                borderColor: isSelected ? 'rgb(229, 83, 61)' : undefined,
              },
            }}
            aria-label="Select result"
          />
        )}
        <IconUser size={16} className="mt-1 shrink-0 text-violet-500" />
        {item.isNew && <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />}
        <div className="flex flex-col leading-tight">
          <PatientNameLink item={item} />
          {dateLabel && <span className="text-xs text-gray-500">{dateLabel}</span>}
          {(item.isAbnormal || item.codeText) && (
            <span className="text-xs text-gray-500">
              {item.isAbnormal && (
                <>
                  <span className="font-semibold text-red-500">Abn</span>
                  {item.codeText ? ', ' : ''}
                </>
              )}
              {item.codeText}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {item.severity !== 'available' && (
          <span
            className={`text-[10px] font-semibold tracking-wider uppercase ${config.labelColor}`}
          >
            {config.label}
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => item.id && onExpand(item.id)}
            className="flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-[3px] !text-[11px] font-semibold text-white transition-colors"
            style={{ backgroundColor: 'rgb(229, 83, 61)' }}
          >
            {isExpanded ? 'Close' : 'View'} <span aria-hidden="true">{isExpanded ? '↑' : '→'}</span>
          </button>
          {isClosed ? (
            <button
              type="button"
              onClick={() => onRestore(item)}
              className="flex cursor-pointer items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
            >
              Open
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onDismiss([item])}
              className="flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-gray-600 transition-colors hover:bg-gray-50"
            >
              <IconX size={11} />
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function ImagingRow({
  item,
  isClosed,
  isSelected,
  isExpanded,
  onExpand,
  onDismiss,
  onRestore,
  onSelectToggle,
}: RowProps) {
  const config = IMAGING_SEVERITY_CONFIG[item.severity === 'abnormal' ? 'abnormal' : 'available']
  const dateLabel = formatResultDate(item.effectiveAt)
  const primaryLine = [item.codeText, dateLabel].filter(Boolean).join(' · ')

  return (
    <div
      className={`relative flex items-center justify-between border-b border-gray-100 px-4 py-3 ${isClosed ? 'opacity-60' : ''}`}
    >
      <span className={`absolute top-0 bottom-0 left-0 w-[3px] ${config.bar}`} />
      <div className="flex flex-1 items-start gap-2 pl-2">
        {!isClosed && (
          <Checkbox
            size="xs"
            checked={isSelected}
            onChange={() => onSelectToggle(item)}
            styles={{
              input: {
                cursor: 'pointer',
                backgroundColor: isSelected ? 'rgb(229, 83, 61)' : undefined,
                borderColor: isSelected ? 'rgb(229, 83, 61)' : undefined,
              },
            }}
            aria-label="Select result"
          />
        )}
        <IconUser size={16} className="mt-1 shrink-0 text-violet-500" />
        <div className="flex flex-col leading-tight">
          <PatientNameLink item={item} />
          {(item.isAbnormal || primaryLine) && (
            <span className="text-xs text-gray-500">
              {item.isAbnormal && (
                <>
                  <span className="font-semibold text-red-500">Abn</span>
                  {primaryLine ? ', ' : ''}
                </>
              )}
              {primaryLine}
            </span>
          )}
          {item.conclusion && <span className="text-xs text-gray-500">{item.conclusion}</span>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {item.severity !== 'available' && (
          <span
            className={`text-[10px] font-semibold tracking-wider uppercase ${config.labelColor}`}
          >
            {config.label}
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => item.id && onExpand(item.id)}
            className="flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-[3px] !text-[11px] font-semibold text-white transition-colors"
            style={{ backgroundColor: 'rgb(229, 83, 61)' }}
          >
            {isExpanded ? 'Close' : 'View'} <span aria-hidden="true">{isExpanded ? '↑' : '→'}</span>
          </button>
          {isClosed ? (
            <button
              type="button"
              onClick={() => onRestore(item)}
              className="flex cursor-pointer items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
            >
              Open
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onDismiss([item])}
              className="flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 px-2.5 py-[3px] !text-[11px] !font-semibold text-gray-600 transition-colors hover:bg-gray-50"
            >
              <IconX size={11} />
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

type CategoryState = {
  results: ResultItem[]
  total?: number
  hasMore: boolean
  isLoading: boolean
}

const INITIAL_CATEGORY_STATE: CategoryState = {
  results: [],
  total: undefined,
  hasMore: false,
  isLoading: false,
}

type UseResultsArgs = {
  /** False holds the request (and the loading state) until the caller is ready. */
  enabled: boolean
  middleware: ReturnType<typeof useMiddlewareClient>
  patientId: string | undefined
  practitionerIds?: string[]
  dateFrom: string | null
  dateTo: string | null
  showClosed: boolean
  refreshVersion?: number
}

function useResults({
  enabled,
  middleware,
  patientId,
  practitionerIds,
  dateFrom,
  dateTo,
  showClosed,
  refreshVersion,
}: UseResultsArgs) {
  const [state, setState] = useState<CategoryState>(INITIAL_CATEGORY_STATE)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    setOffset(0)
  }, [patientId, practitionerIds, dateFrom, dateTo, showClosed, refreshVersion])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const params: ListResultsParams = {
      count: PAGE_SIZE,
      offset,
      ...(patientId ? { patientIds: [patientId] } : {}),
      ...(practitionerIds?.length ? { practitionerIds } : {}),
      ...(dateFrom ? { dateFrom: dayjs(dateFrom).startOf('day').toISOString() } : {}),
      ...(dateTo ? { dateTo: dayjs(dateTo).endOf('day').toISOString() } : {}),
      ...(showClosed ? { showClosed } : {}),
    }

    setState((prev) => ({ ...prev, isLoading: true }))

    fetchResults(middleware, params)
      .then((response) => {
        if (cancelled) return
        setState((prev) => ({
          results: offset === 0 ? response.results : [...prev.results, ...response.results],
          total: response.total,
          hasMore: response.hasMore,
          isLoading: false,
        }))
      })
      .catch((err) => {
        if (cancelled) return
        logError('home', 'Failed to fetch results', err)
        setState((prev) => ({ ...prev, isLoading: false }))
      })

    return () => {
      cancelled = true
    }
  }, [
    enabled,
    middleware,
    patientId,
    practitionerIds,
    dateFrom,
    dateTo,
    showClosed,
    refreshVersion,
    offset,
  ])

  const loadMore = useCallback(() => {
    setOffset((prev) => prev + PAGE_SIZE)
  }, [])

  // A held request is still pending, not an empty result.
  return { ...state, isLoading: state.isLoading || !enabled, loadMore }
}

type ResultsProps = {
  practitionerIds?: string[]
}

export const Results = ({ practitionerIds }: ResultsProps) => {
  const middleware = useMiddlewareClient()
  const profile = useMedplumProfile()
  const currentPractitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined
  const { canOrder: canFilterMyReferrals, loading: roleLoading } = useCanOrder()
  // The other two chips in this filter row are session-kept, so these have to be
  // too — a row where some chips survive a tab switch and others silently do not
  // stops describing the list it filters (PD-1206).
  // `null` until the user toggles it or clears filters: providers (those who can
  // order) then get it ON by default, everyone else OFF (PD-1877). Once chosen, the
  // choice sticks for the session — the default never re-enables it.
  const [myReferralsChoice, setMyReferralsChoice] = useSessionState<boolean | null>(
    'home:results:myReferralsOnly',
    null,
  )
  const myReferralsOnly = canFilterMyReferrals && (myReferralsChoice ?? true)
  const [selectedPatient, setSelectedPatient] = useSessionState<Patient | null>(
    'home:results:selectedPatient',
    null,
  )
  // Remount counter for PatientSelect, not user state — deliberately not kept.
  const [patientSelectKey, setPatientSelectKey] = useState(0)
  const patientId = selectedPatient?.id

  // Expand/collapse and the section filters are kept for the session so leaving
  // Home and coming back reopens Labs & Imaging as the user left it (PD-1206).
  const [open, setOpen] = useSessionState('home:results:open', true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showClosed, setShowClosed] = useSessionState('home:results:showClosed', false)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [restoredIds, setRestoredIds] = useState<Set<string>>(new Set())
  const [pendingDismiss, setPendingDismiss] = useState<ResultItem[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const refreshResults = () => {
    setDismissedIds(new Set())
    setRestoredIds(new Set())
    setSelectedIds(new Set())
    setRefreshVersion((v) => v + 1)
  }

  const handleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const [storedDateFrom, setDateFrom] = useSessionState<string | null>(
    'home:results:dateFrom',
    defaultDateFromForPreset(),
  )
  const [storedDateTo, setDateTo] = useSessionState<string | null>(
    'home:results:dateTo',
    dayjs().format(DATE_FORMAT),
  )
  // Preset identity is separate from the picker values so an explicit preset
  // follows today across midnight, while a same-shaped hand-picked range stays
  // exactly where the user put it (PD-1206).
  const [activePreset, setActivePreset] = useSessionState<PresetId | null>(
    'home:results:activePreset',
    DEFAULT_PRESET_ID,
  )
  const { dateFrom, dateTo } = resolvePresetRange(activePreset, storedDateFrom, storedDateTo)

  const effectivePractitionerIds = useMemo(
    () =>
      myReferralsOnly && canFilterMyReferrals && currentPractitionerId
        ? [currentPractitionerId]
        : practitionerIds,
    [myReferralsOnly, canFilterMyReferrals, currentPractitionerId, practitionerIds],
  )

  const results = useResults({
    // Held until the role is known, so a provider's first request is already narrowed
    // to their referrals instead of flashing everyone's results first (PD-1877).
    enabled: !roleLoading,
    middleware,
    patientId,
    practitionerIds: effectivePractitionerIds,
    dateFrom,
    dateTo,
    showClosed: showClosed ?? false,
    refreshVersion,
  })

  const handleDismiss = async (item: ResultItem) => {
    setDismissedIds((prev) => new Set([...prev, item.id]))
    setRestoredIds((prev) => {
      const next = new Set(prev)
      next.delete(item.id)
      return next
    })
    try {
      await closeResult(middleware, item.id)
    } catch (err) {
      setDismissedIds((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
      logError('home', 'Failed to dismiss result', err)
      showNotification({
        color: 'red',
        title: 'Unable to dismiss result',
        message: normalizeErrorString(err),
      })
      refreshResults()
    }
  }

  const handleRestore = async (item: ResultItem) => {
    setRestoredIds((prev) => new Set([...prev, item.id]))
    setDismissedIds((prev) => {
      const next = new Set(prev)
      next.delete(item.id)
      return next
    })
    try {
      await uncloseResult(middleware, item.id)
    } catch (err) {
      setRestoredIds((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
      logError('home', 'Failed to restore result', err)
      showNotification({
        color: 'red',
        title: 'Unable to restore result',
        message: normalizeErrorString(err),
      })
      refreshResults()
    }
  }

  const applyPreset = (presetId: PresetId) => {
    setActivePreset(presetId)
    setDateFrom(presetIdToDateFrom(presetId))
    setDateTo(dayjs().format(DATE_FORMAT))
  }

  const handleDateFromChange = (value: string | null) => {
    setActivePreset(null)
    setDateFrom(value)
    setDateTo(dateTo)
  }

  const handleDateToChange = (value: string | null) => {
    setActivePreset(null)
    setDateFrom(dateFrom)
    setDateTo(value)
  }

  const clearFilters = () => {
    setActivePreset(null)
    setDateFrom(null)
    setDateTo(null)
    setSelectedPatient(null)
    setPatientSelectKey((k) => k + 1)
    setMyReferralsChoice(false)
  }

  const hasFilters =
    dateFrom !== null || dateTo !== null || Boolean(selectedPatient) || myReferralsOnly

  const newCount = useMemo(() => results.results.filter((r) => r.isNew).length, [results.results])

  return (
    <div className="flex flex-col gap-3">
      <div className="shrink-0">
        <PatientSelect
          key={patientSelectKey}
          setSelectedPatient={setSelectedPatient}
          initialPatient={selectedPatient}
          placeholder="Filter by patient (optional)"
          hideAddPatientButton
        />
      </div>

      <CollapsibleSectionCard
        icon={<IconMicroscope size={20} className="text-teal-600" />}
        title="Labs & Imaging"
        count={results.total ?? results.results.length}
        countClassName="text-amber-600"
        headerExtras={
          newCount > 0 ? (
            <>
              <span className="h-2 w-2 rounded-full bg-red-400" />
              <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-emerald-700 uppercase">
                New
              </span>
            </>
          ) : null
        }
        open={open}
        onToggle={() => setOpen((v) => !v)}
      >
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-4 pt-2 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterPills options={PRESET_FILTERS} value={activePreset} onChange={applyPreset} />
            {canFilterMyReferrals && (
              <button
                type="button"
                onClick={() => setMyReferralsChoice(!myReferralsOnly)}
                className={[
                  'flex cursor-pointer items-center gap-1 rounded-full px-2 !py-[1px] !text-[12px] transition-colors',
                  myReferralsOnly
                    ? '!font-semibold text-white'
                    : 'border border-gray-300 font-normal text-gray-700 hover:bg-gray-50',
                ].join(' ')}
                style={myReferralsOnly ? { backgroundColor: 'rgb(13, 148, 136)' } : undefined}
              >
                {myReferralsOnly && <IconCheck size={11} />}
                My referrals only
              </button>
            )}
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="cursor-pointer rounded-full border border-gray-300 px-2 !py-[1px] !text-[12px] font-normal text-gray-700 transition-colors hover:bg-gray-50"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              size="xs"
              label="Show dismissed"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.currentTarget.checked)}
              styles={{
                label: { fontSize: 12, cursor: 'pointer' },
                input: {
                  cursor: 'pointer',
                  backgroundColor: showClosed ? 'rgb(229, 83, 61)' : undefined,
                  borderColor: showClosed ? 'rgb(229, 83, 61)' : undefined,
                },
              }}
            />
            <DateRangeRow
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={handleDateFromChange}
              onDateToChange={handleDateToChange}
            />
          </div>
        </div>
        {(() => {
          const openIds = results.results
            .filter(
              (i) =>
                !((i.isClosedByCurrentUser || dismissedIds.has(i.id)) && !restoredIds.has(i.id)),
            )
            .map((i) => i.id)
          const selectedCount = openIds.filter((id) => selectedIds.has(id)).length
          const allSelected = openIds.length > 0 && selectedCount === openIds.length
          const someSelected = selectedCount > 0 && !allSelected
          if (openIds.length === 0) return null
          return (
            <div className="flex shrink-0 items-center justify-between gap-3 border-y border-gray-100 bg-gray-50 px-4 py-2">
              <Checkbox
                size="xs"
                label="Select all open items"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) =>
                  setSelectedIds(e.currentTarget.checked ? new Set(openIds) : new Set())
                }
                styles={{
                  label: { fontSize: 12, cursor: 'pointer' },
                  input: {
                    cursor: 'pointer',
                    backgroundColor: allSelected || someSelected ? 'rgb(229, 83, 61)' : undefined,
                    borderColor: allSelected || someSelected ? 'rgb(229, 83, 61)' : undefined,
                  },
                }}
              />
              {selectedCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-600">{selectedCount} selected</span>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="cursor-pointer rounded-md border border-gray-300 px-2 py-[2px] text-[10px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Clear selection
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const items = results.results.filter((i) => selectedIds.has(i.id))
                      setPendingDismiss(items)
                    }}
                    className="cursor-pointer rounded-md px-2 py-[2px] text-[10px] font-semibold text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: 'rgb(229, 83, 61)' }}
                  >
                    Dismiss selected
                  </button>
                </div>
              )}
            </div>
          )
        })()}
        <div className="relative min-h-0 flex-1 overflow-y-auto">
          <LoadingOverlay
            visible={results.isLoading && results.results.length === 0}
            zIndex={10}
            overlayProps={{ blur: 1 }}
            loaderProps={{ size: 'sm', color: 'blue' }}
          />
          {results.results.length === 0 && !results.isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-4 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50">
                <IconSearch size={20} className="text-orange-500" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-900">
                  No results found for this date range
                </p>
                <p className="max-w-sm text-xs text-gray-500">
                  Results may be hidden by the active filters. Try expanding the date range or
                  clearing all filters.
                </p>
              </div>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-1 cursor-pointer rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            results.results
              .filter((item) =>
                showClosed
                  ? true
                  : !(
                      (item.isClosedByCurrentUser || dismissedIds.has(item.id)) &&
                      !restoredIds.has(item.id)
                    ),
              )
              .map((item) => {
                const isClosed =
                  (item.isClosedByCurrentUser || dismissedIds.has(item.id)) &&
                  !restoredIds.has(item.id)
                return (
                  <React.Fragment key={item.id}>
                    {item.category === 'lab' ? (
                      <LabRow
                        item={item}
                        isClosed={isClosed}
                        isSelected={selectedIds.has(item.id)}
                        isExpanded={expandedId === item.id}
                        onExpand={handleExpand}
                        onDismiss={setPendingDismiss}
                        onRestore={handleRestore}
                        onSelectToggle={(it) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(it.id)) next.delete(it.id)
                            else next.add(it.id)
                            return next
                          })
                        }}
                      />
                    ) : (
                      <ImagingRow
                        item={item}
                        isClosed={isClosed}
                        isSelected={selectedIds.has(item.id)}
                        isExpanded={expandedId === item.id}
                        onExpand={handleExpand}
                        onDismiss={setPendingDismiss}
                        onRestore={handleRestore}
                        onSelectToggle={(it) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(it.id)) next.delete(it.id)
                            else next.add(it.id)
                            return next
                          })
                        }}
                      />
                    )}
                    {expandedId === item.id && <ExpandedResultDetail reportId={item.id} />}
                  </React.Fragment>
                )
              })
          )}
          {results.hasMore && (
            <div className="flex items-center justify-center px-4 py-3">
              {results.isLoading ? (
                <Loader size="sm" color="blue" />
              ) : (
                <button
                  type="button"
                  onClick={results.loadMore}
                  className="rounded-md border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Load more
                </button>
              )}
            </div>
          )}
        </div>
      </CollapsibleSectionCard>

      <Modal
        opened={pendingDismiss.length > 0}
        onClose={() => setPendingDismiss([])}
        centered
        size="sm"
        radius="md"
        title={
          <span className="text-sm font-semibold text-gray-900">
            Dismiss result{pendingDismiss.length > 1 ? 's' : ''}
          </span>
        }
      >
        {pendingDismiss.length === 1 ? (
          <p className="mb-1 text-sm text-gray-700">
            Are you sure you want to dismiss this result
            {pendingDismiss[0]?.patientDisplayName ? (
              <>
                {' '}
                for <span className="font-semibold">{pendingDismiss[0].patientDisplayName}</span>
              </>
            ) : null}
            ?
          </p>
        ) : (
          <p className="mb-1 text-sm text-gray-700">
            Are you sure you want to dismiss{' '}
            <span className="font-semibold">{pendingDismiss.length} results</span>?
          </p>
        )}
        <p className="mb-5 text-xs text-gray-500">
          {pendingDismiss.length === 1 ? 'It' : 'They'} will be hidden from your view. You can
          restore {pendingDismiss.length === 1 ? 'it' : 'them'} at any time using "Show dismissed".
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setPendingDismiss([])}
            className="cursor-pointer rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void Promise.all(pendingDismiss.map((item) => handleDismiss(item)))
              setSelectedIds(new Set())
              setPendingDismiss([])
            }}
            className="cursor-pointer rounded-md px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: 'rgb(229, 83, 61)' }}
          >
            Dismiss
          </button>
        </div>
      </Modal>
    </div>
  )
}
